import { FederatedEvent, IDestroyOptions, Renderer, Text } from "pixi.js";
import { Game } from "../Game";
import { CityverseTycoonContext } from "./composition/CityverseTycoonContext";
import { GameInfo, Stage } from "../analytics/GameInfo";
import { GuestAuthInfo } from "./scenes/common/models/GuestAuthInfo";
import { PromiseCompletionSource } from "../shared/async/PromiseCompletionSource";
import { SceneManager } from "../scene/SceneManager";
import { CityverseTycoonViewableLayer } from "./scenes/layers/CityverseTycoonViewableLayer";
import { CityverseTycoonNotificationLayer } from "./scenes/layers/CityverseTycoonNotificationLayer";
import { Logger } from "./logger/Logger";
import { ConsoleLogger } from "./logger/ConsoleLogger";
import { SentryLogger } from "./logger/SentryLogger";
import { Sentry } from "../analytics/Sentry";
import { LogLevel } from "./logger/LogLevel";
import { PreloaderModule } from "./scenes/preloader/module/PreloaderModule";
import { CityverseTycoonTelemetryLayer } from "./scenes/layers/CityverseTycoonTelemetryLayer";
import { TelemetryView } from "./scenes/game/components/dev/TelemetryView";
import { PathHelper } from "../tools/PathHelper";
import { ReloadTracker } from "./trackers/ReloadTracker";
import { PerformanceTracker } from "./trackers/PerformanceTracker";
import { VariableSaver } from "../tools/variableSaver/VariableSaver";

export class CityverseTycoon extends Game {
    private readonly cityverseTycoonContext: CityverseTycoonContext;
    private readonly gameInfo: GameInfo;
    private readonly logger: Logger;
    private readonly sentry: Sentry;
    private readonly reloadTracker?: ReloadTracker;

    public constructor(gameView: HTMLCanvasElement) {
        super(gameView);
        this.registerTextDestroyHack();
        this.fixMemoryLeakInTrackingData();
        this.cityverseTycoonContext = new CityverseTycoonContext();
        this.gameInfo = new GameInfo();
        this.sentry = new Sentry(this.gameInfo);
        this.logger = this.initializeLogger();
        if (this.gameInfo.debug) {
            //@ts-ignore
            globalThis.__PIXI_APP__ = this;
        }
        console.log(`App started, version: ${this.gameInfo.gameVersion}, debug: ${this.gameInfo.debug}, stage: ${this.gameInfo.stage}`);
        if (this.gameInfo.stage !== Stage.LOCALHOST) {
            this.reloadTracker = new ReloadTracker(this.gameInfo);
        }
    }

    public override async launch(): Promise<void> {
        await super.launch(new SceneManager(this.stage, {
            viewableLayerType: new CityverseTycoonViewableLayer(this.canvasScaler, this.renderer as Renderer),
            notificationLayerType: new CityverseTycoonNotificationLayer(this.canvasScaler),
            telemetryLayer: this.gameInfo.debug ? new CityverseTycoonTelemetryLayer(this.canvasScaler) : null
        }), PathHelper.relativePath(`assets/manifest-${this.gameInfo.gameVersion}.json`));
        const waitGuestInfo = new PromiseCompletionSource<GuestAuthInfo>();
        this.fetchGuestInfo(waitGuestInfo);
        this.reloadTracker?.startBackgroundTrack();

        this.cityverseTycoonContext.build(this.canvasScaler, this.sceneManager, this.gameInfo, this.renderer as Renderer, waitGuestInfo, this.sentry, this.logger);
        try {
            this.setupPerformanceTracker();
            const preloaderModule = await this.cityverseTycoonContext.resolve(PreloaderModule);
            preloaderModule.start();
            this.setupTelemetry();

        } catch (error: unknown) {
            this.logger.error(error);
        }
    }

    private setupTelemetry(): void {
        if (!this.gameInfo.debug) {
            this.logger.debug("Telemetry disabled for non-dev builds");
            return;
        }
        const telemetryQuery = "telemetry=true";
        if (!location.href.includes(telemetryQuery)) {
            this.logger.debug("Telemetry disabled, to enable, add &telemetry=true to url");
            return;
        }

        new TelemetryView(this.sceneManager.telemetryLayer, this.canvasScaler);
        this.logger.debug("Telemetry enabled");
    }

    private async fetchGuestInfo(waitGuestInfo: PromiseCompletionSource<GuestAuthInfo>): Promise<void> {
        const guestInfo = await GuestAuthInfo.fromProvidedInfo(this.ticker);
        waitGuestInfo.complete(guestInfo);
        this.sentry.addTag("guest_id", guestInfo.id);
    }

    private registerTextDestroyHack(): void {
        const originTextDestroy = Text.prototype.destroy;
        Text.prototype.destroy = function (options?: boolean | IDestroyOptions): void {
            originTextDestroy.call(this, true);
        }
    }

    private fixMemoryLeakInTrackingData(): void {
        const rootBoundary = this.renderer.events.rootBoundary;
        const customEventMapping = (function (e: FederatedEvent): void {
            const pointerId = (e as any)["pointerId"];
            if (this.mappingState.trackingData[pointerId]) {
                delete this.mappingState.trackingData[pointerId];
            }
        }).bind(rootBoundary);

        rootBoundary.addEventMapping("pointerleave", customEventMapping);
        rootBoundary.addEventMapping("pointerout", customEventMapping);
    }

    private initializeLogger(): Logger {
        const commonIgnoreLevels: Array<LogLevel> = [];
        const sentryIgnoreLevels: Array<LogLevel> = [LogLevel.Debug, LogLevel.Info, LogLevel.Warn];

        if (!this.gameInfo.debug) {
            commonIgnoreLevels.push(LogLevel.Debug, LogLevel.Warn);
        }

        return new Logger([new ConsoleLogger(), new SentryLogger(this.sentry, sentryIgnoreLevels)], commonIgnoreLevels);
    }

    private async setupPerformanceTracker(): Promise<void> {
        const variableSaver = await this.cityverseTycoonContext.resolve(VariableSaver);
        new PerformanceTracker(variableSaver, this.logger);
    }
}
